import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { touchpointKeys } from "hooks/api/query-keys";
import { useRewriteTouchpointsWithWriter } from "hooks/api/use-touchpoints";

import type {
  RewriteTouchpointContentsDTO,
  RewriteTouchpointContentsRequest,
} from "generated/schemas";

import type { TouchpointListResult } from "utilities/api-mappers";
import { markEmailWriterPollingStarted } from "utilities/email-writer-polling-session";

type QueueCreativesRewriteParams = {
  clientId: string;
  campaignId: string;
  requests: RewriteTouchpointContentsRequest[];
};

export type QueueCreativesRewriteResult = RewriteTouchpointContentsDTO & {
  failedRequestCount: number;
};

export const useQueueCreativesRewrite = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: rewriteTouchpoints, isPending: isQueueing } =
    useRewriteTouchpointsWithWriter();

  /**
   * Sends every request and starts completion polling for the queued creatives.
   * Rejects only when every request failed; partial failures are reported in `failedRequestCount`
   * because the creatives queued by the other requests are already being written.
   */
  const queueRewrites = useCallback(
    async ({ clientId, campaignId, requests }: QueueCreativesRewriteParams): Promise<QueueCreativesRewriteResult> => {
      const settled = await Promise.allSettled(
        requests.map((request) => rewriteTouchpoints({ clientId, campaignId, ...request })),
      );
      const fulfilled = settled.flatMap((result) => (result.status === "fulfilled" ? [result.value] : []));
      const queued = fulfilled.flatMap((result) => result.queued);
      const skipped = fulfilled.flatMap((result) => result.skipped);
      const failedRequestCount = settled.length - fulfilled.length;

      if (queued.length > 0) {
        markEmailWriterPollingStarted(
          campaignId,
          clientId,
          queued.map((touchpoint) => touchpoint.id),
        );

        const queuedById = new Map(queued.map((touchpoint) => [touchpoint.id, touchpoint]));

        queryClient.setQueriesData<TouchpointListResult>(
          { queryKey: touchpointKeys.lists() },
          (data) =>
            data && {
              ...data,
              items: data.items.map((item) => {
                const queuedTouchpoint = queuedById.get(item.id);

                return queuedTouchpoint
                  ? {
                      ...item,
                      batchJobExecutionStatus: queuedTouchpoint.batchJobExecutionStatus,
                      batchJobType: queuedTouchpoint.batchJobType,
                    }
                  : item;
              }),
            },
        );
      }

      const rejected = settled.find((result) => result.status === "rejected");
      if (rejected && fulfilled.length === 0) {
        throw rejected.reason;
      }

      return { queued, skipped, failedRequestCount };
    },
    [queryClient, rewriteTouchpoints],
  );

  return { queueRewrites, isQueueing };
};
